import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import { Account, AccountErrorMessages, AppConfiguration, LocalAccountStorage, Message, MessageHandlerDisplayType, PrivateRoutes, useAppDispatch, useIsRecoverySetUpQuery, useAppSelector } from "src/common";
import { Icon } from "src/components";
import { accountActions, uiActions } from "src/store";
import { RootState } from "src/store/configureStore";

type AllProps = {
  active: boolean;
  active_path?: string;
  current_account: Account;
};

// Description: Settings Dropdown
function SettingsDropdown(props: AllProps) {
  const { active, active_path, current_account } = props;
  const dispatch = useAppDispatch();
  const recovery_code_pending = useAppSelector((state: RootState) => state.settings.recovery_code_pending);
  const recovery_group_set = useAppSelector((state: RootState) => state.account.current_account?.recovery_group)?.group_set;
  const pending_approvals = useAppSelector((state: RootState) => state.settings.pending_approvals);
  const organization = useAppSelector((state: RootState) => state.account.current_account)?.org_info;
  const auth_token = useAppSelector((state: RootState) => state.account.auth_token);
  const express_recovery_not_setup = useAppSelector((state: RootState) => state.account.express_recovery_not_setup);
  const [show, setShow] = useState(false);
  const { data, error } = useIsRecoverySetUpQuery({ user_id: current_account.user_id, auth_token: auth_token || "" }, { skip: !auth_token });

  // Description: Checks to see if express recovery is set up in the backend - if not set up, a notification dot is displayed
  // This query is only called when an auth token exists (only for express users)
  useEffect(() => {
    if (!!data) {
      if (!data.server_shard_exists) {
        LocalAccountStorage.setTemporaryCookies("express_recovery_not_setup", "true");
        dispatch(accountActions.setExpressRecoveryNotSetup("true"));
      }
    }
    !!error && dispatch(uiActions.handleRequestErrors(new Message(AccountErrorMessages.error_fetching_express_recovery_notification, MessageHandlerDisplayType.logger), error));
  }, [error, data]);

  return (
    // active
    <Dropdown className={`nav-item${active ? " active" : ""} ${!!express_recovery_not_setup ? "dot" : ""}`} show={show} onToggle={setShow}>
      <Dropdown.Toggle as="a"
        aria-label="My Account Settings"
        aria-haspopup="menu"
        aria-expanded={show}>
        <Icon className="ficon-settings show-desktop" />
        <span className="hide-desktop">Settings</span>
      </Dropdown.Toggle>
      <Dropdown.Menu align="end">
        <Dropdown.Item
          as={Link}
          aria-label="User Profile"
          className={`${active_path === PrivateRoutes.profile_route ? " active" : ""}`}
          to={`/${PrivateRoutes.settings_route}/${PrivateRoutes.profile_route}`}>
          Profile
        </Dropdown.Item>
        {!!AppConfiguration.buildForExpress() &&
          <>
            <Dropdown.Item
              as={Link}
              aria-label="Account Recovery"
              className={`${active_path === PrivateRoutes.password_recovery_route ? " active" : ""} ${!!express_recovery_not_setup ? "dot" : ""}`}
              to={`/${PrivateRoutes.settings_route}/${PrivateRoutes.password_recovery_route}`}>
              Account Recovery
            </Dropdown.Item>
            <Dropdown.Item
              as={Link}
              aria-label="Change Password"
              className={`${active_path === PrivateRoutes.change_password_route ? " active" : ""}`}
              to={`/${PrivateRoutes.settings_route}/${PrivateRoutes.change_password_route}`}>
              Change Password
            </Dropdown.Item>
            <Dropdown.Item
              as={Link}
              aria-label="Change Authentication"
              className={`${active_path === PrivateRoutes.change_authentication_route ? " active" : ""}`}
              to={`/${PrivateRoutes.settings_route}/${PrivateRoutes.change_authentication_route}`}>
              Change Authentication
            </Dropdown.Item>
          </>
        }
        {/* "dot" for notifications */}
        {!!AppConfiguration.buildForApp() && (
          <>
            <Dropdown.Item
              as={Link}
              className={`${active_path === PrivateRoutes.approvals_route ? " active" : ""} ${pending_approvals > 0 ? "notification-item" : ""}`}
              aria-label="Approvals"
              to={`/${PrivateRoutes.settings_route}/${PrivateRoutes.approvals_route}`}>
              Approvals
              {pending_approvals > 0 && <span className="blue">{pending_approvals}</span>}
            </Dropdown.Item>
            <Dropdown.Item
              as={Link}
              className={`${active_path === PrivateRoutes.activity_route ? " active" : ""}`}
              aria-label="Activity Logs"
              to={`/${PrivateRoutes.settings_route}/${PrivateRoutes.activity_route}`}>
              Activity Logs
            </Dropdown.Item>
            {
              (!organization || Account.isShowMUAPrompt(organization)) &&
              <Dropdown.Item
                as={Link}
                className={`${active_path === PrivateRoutes.install_profile_route ? " active" : ""}`}
                aria-label="Add to Mail Client"
                to={`/${PrivateRoutes.settings_route}/${PrivateRoutes.install_profile_route}`}>
                Add to Mail Client
              </Dropdown.Item>
            }
            <Dropdown.Divider />
            <Dropdown.Item
              as={Link}
              className={`${active_path === PrivateRoutes.device_management_route ? " active" : ""}`}
              aria-label="Device Management"
              to={`/${PrivateRoutes.settings_route}/${PrivateRoutes.device_management_route}`}>
              Device Management
            </Dropdown.Item>
            <Dropdown.Item
              as={Link}
              className={`${active_path === PrivateRoutes.add_device_route ? " active" : ""}`}
              aria-label="Add Device"
              to={`/${PrivateRoutes.settings_route}/${PrivateRoutes.add_device_route}`}>
              Add Device
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item
              as={Link}
              className={`${active_path === PrivateRoutes.recovery_route ? " active" : ""} ${!recovery_group_set ? "notification-item" : ""}`}
              aria-label="Recovery Group"
              to={`/${PrivateRoutes.settings_route}/${PrivateRoutes.recovery_route}`}>
              Recovery Group
              {!recovery_group_set && <span className="red">!</span>}
            </Dropdown.Item>
            <Dropdown.Item
              as={Link}
              aria-label="Account Recovery"
              className={`${active_path === PrivateRoutes.account_backup_route ? " active" : ""} ${!!recovery_code_pending ? "notification-item" : ""}`}
              to={`/${PrivateRoutes.settings_route}/${PrivateRoutes.account_backup_route}`}>
              Account Recovery
              {!!recovery_code_pending && <span className="red">!</span>}
            </Dropdown.Item>
            <Dropdown.Item
              as={Link}
              className={`${active_path === PrivateRoutes.approve_recovery_route ? " active" : ""}`}
              aria-label="Recovery Approval"
              to={`/${PrivateRoutes.settings_route}/${PrivateRoutes.approve_recovery_route}`}>
              Recovery Approval
            </Dropdown.Item>{" "}
          </>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default React.memo(SettingsDropdown);
